import { ChevronUp, RotateCcw } from "feather-icons-react/build/IconComponents";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { setToogleHeader } from "../../core/redux/action";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { message } from "antd";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {
  AllCommunityModule,
  ModuleRegistry,
  provideGlobalGridOptions,
} from "ag-grid-community";
import { getCurrentUser } from "../../core/api/settings";
import { fetchTigerTechSales, syncTigerTechSales } from "../../core/api/data";
import formatDateWithTimezone from "../../utils/dateUtils";
import { dateFilterParams } from "../../utils/dateUtils";

ModuleRegistry.registerModules([AllCommunityModule]);
provideGlobalGridOptions({ theme: "legacy" });

const TigerTechOrders = ({ timezone, filters, refreshTrigger }) => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  useEffect(() => {
    if (filters && filters.brands && filters.brands.length > 0) {
      setLoading(true);
      fetchOrdersData(filters.brands[0], filters);
    } else {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (refreshTrigger > 0 && filters && filters.brands && filters.brands.length > 0) {
      setLoading(true);
      fetchOrdersData(filters.brands[0], filters);
    }
  }, [refreshTrigger]);

 
  const fetchOrdersData = (brandId, currentFilters = null) => {
    setLoading(true);
    
    // Add source parameter for TigerTech
    const fetchPromise = currentFilters
      ? fetchTigerTechSales(brandId, {...currentFilters, source: 'TigerTech'})
      : fetchTigerTechSales(brandId, {source: 'TigerTech'});
      
    fetchPromise
      .then((data) => {
        setRowData(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching TigerTech orders:", error);
        message.error("Failed to fetch TigerTech orders");
        setLoading(false);
      });
  };

  const handleSyncTigerTech = () => {
    if (!filters || !filters.brands || filters.brands.length === 0) {
      message.error("Please select a brand first");
      return;
    }

    setSyncInProgress(true);
    message.loading({ content: "Syncing TigerTech orders...", key: "tigerTechSync", duration: 0 });

    // Add source parameter for TigerTech
    syncTigerTechSales(filters.brands[0], 'TigerTech')
      .then((response) => {
        if (response.error) {
          message.error({ content: response.error, key: "tigerTechSync" });
          setSyncInProgress(false);
        } else {
          // Check if we have imported/skipped counts or use default message
          let successMsg;
          if (response.imported !== undefined && response.skipped !== undefined) {
            successMsg = `TigerTech orders synced successfully. Imported: ${response.imported}, Skipped: ${response.skipped}`;
          } else {
            successMsg = response.message || "TigerTech orders synced successfully";
          }
          message.success({ content: successMsg, key: "tigerTechSync" });
          setLastSyncTime(new Date().toLocaleString());
          
          // Refresh the data after successful sync
          fetchOrdersData(filters.brands[0], filters);
          setSyncInProgress(false);
        }
      })
      .catch((error) => {
        console.error("Error syncing TigerTech orders:", error);
        message.error({ content: "Failed to sync TigerTech orders", key: "tigerTechSync" });
        setSyncInProgress(false);
      });
  };

  // Define columns for AG Grid
  const columns = [
    {
      headerName: "#",
      field: "id",
      sortable: true,
      filter: true,
      width: 80,
      resizable: true,
    },
    {
      headerName: "Customer Number",
      field: "customer_number",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Order Number",
      field: "order_number",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Order Date",
      field: "order_date",
      sortable: true,
      filter: 'agDateColumnFilter',
      filterParams: dateFilterParams,
      width: 150,
      resizable: true,
      valueFormatter: (params) => formatDateWithTimezone(params.value, timezone),
    },
    {
      headerName: "Bill Last Name",
      field: "bill_last_name",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Bill First Name",
      field: "bill_first_name",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Bill Company",
      field: "bill_company",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Bill Address 1",
      field: "bill_address1",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Bill Address 2",
      field: "bill_address2",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Bill City",
      field: "bill_city",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Bill State",
      field: "bill_state",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Bill Zipcode",
      field: "bill_zipcode",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Bill Phone Number",
      field: "bill_phone_number",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "ACH Bank Name",
      field: "ach_bank_name",
      sortable: true,
      filter: true,
      width: 180,
      resizable: true,
    },
    {
      headerName: "No Soliciting",
      field: "no_soliciting",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Payment Type",
      field: "payment_type",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "CC Type",
      field: "cc_type",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "CC Number",
      field: "cc_number",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Exp Date",
      field: "exp_date",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "DNIS",
      field: "dnis",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Key Code",
      field: "key_code",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Emp Number",
      field: "emp_number",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Shipping Method",
      field: "shipping_method",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Order Already Fulfilled",
      field: "order_already_fullfilled",
      sortable: true,
      filter: true,
      width: 180,
      resizable: true,
    },
    {
      headerName: "Amount Already Paid",
      field: "amount_already_paid",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return '';
        return `$${parseFloat(params.value).toFixed(2)}`;
      },
    },
    {
      headerName: "Continued",
      field: "continued",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Product 01",
      field: "product01",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Quantity 01",
      field: "quantity01",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Ship To Last Name",
      field: "ship_to_last_name",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Ship To First Name",
      field: "ship_to_first_name",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Ship To Company",
      field: "ship_to_company",
      sortable: true,
      filter: true,
      width: 180,
      resizable: true,
    },
    {
      headerName: "Ship To Address 1",
      field: "ship_to_address1",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Ship To Address 2",
      field: "ship_to_address2",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Ship To City",
      field: "ship_to_city",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Ship To State",
      field: "ship_to_state",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Ship To Zip",
      field: "ship_to_zip",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Ship To Phone",
      field: "ship_to_phone",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Shipping Country",
      field: "scountry_usa_001",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Shipping Method",
      field: "shipping_method",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Shipping Date",
      field: "shipping_date",
      sortable: true,
      filter: 'agDateColumnFilter',
      filterParams: dateFilterParams,
      width: 150,
      resizable: true,
      valueFormatter: (params) => formatDateWithTimezone(params.value, timezone),
    },
    {
      headerName: "Shipping Carrier",
      field: "shipping_carrier",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Tracking Number",
      field: "tracking_num",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Use Shipping",
      field: "use_shipping",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Shipping Cost",
      field: "shipping",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
      valueFormatter: (params) => params.value ? `$${parseFloat(params.value).toFixed(2)}` : '',
    },
    {
      headerName: "Packing Slip Comments",
      field: "packing_slip_comments",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    
    // Payment Information
    {
      headerName: "Payment Method",
      field: "payment_method",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Payment Type",
      field: "payment_type",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "CC Type",
      field: "cc_type",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "CC Number",
      field: "cc_number",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Exp Date",
      field: "exp_date",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Token",
      field: "token",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Pay Proc Acct",
      field: "pay_proc_acct",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Merchant Transaction ID",
      field: "merchant_transaction_id",
      sortable: true,
      filter: true,
      width: 180,
      resizable: true,
    },
    {
      headerName: "Client Transaction ID",
      field: "client_transaction_id",
      sortable: true,
      filter: true,
      width: 180,
      resizable: true,
    },
    {
      headerName: "CVV Code",
      field: "cvv_code",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    
    // ACH Information
    {
      headerName: "ACH Bank Name",
      field: "ach_bank_name",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "ACH Routing Number",
      field: "ach_routing_number",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "ACH Account Number",
      field: "ach_account_number",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "ACH Check Number",
      field: "ach_check_number",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "ACH Is Savings Account",
      field: "ach_is_savings_account",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    
    // Product Information
    {
      headerName: "Product 1",
      field: "product01",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Quantity 1",
      field: "quantity01",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Price 1",
      field: "price01",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
      valueFormatter: (params) => params.value ? `$${parseFloat(params.value).toFixed(2)}` : '',
    },
    {
      headerName: "Discount 1",
      field: "discount01",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
      valueFormatter: (params) => params.value ? `$${parseFloat(params.value).toFixed(2)}` : '',
    },
    {
      headerName: "Description 1",
      field: "description01",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Coupon Code 1",
      field: "coupon_code01",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    
    {
      headerName: "Product 2",
      field: "product02",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Quantity 2",
      field: "quantity02",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Price 2",
      field: "price02",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
      valueFormatter: (params) => params.value ? `$${parseFloat(params.value).toFixed(2)}` : '',
    },
    {
      headerName: "Discount 2",
      field: "discount02",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
      valueFormatter: (params) => params.value ? `$${parseFloat(params.value).toFixed(2)}` : '',
    },
    {
      headerName: "Description 2",
      field: "description02",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Coupon Code 2",
      field: "coupon_code02",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    
    {
      headerName: "Product 3",
      field: "product03",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Quantity 3",
      field: "quantity03",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Price 3",
      field: "price03",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
      valueFormatter: (params) => params.value ? `$${parseFloat(params.value).toFixed(2)}` : '',
    },
    {
      headerName: "Discount 3",
      field: "discount03",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
      valueFormatter: (params) => params.value ? `$${parseFloat(params.value).toFixed(2)}` : '',
    },
    {
      headerName: "Description 3",
      field: "description03",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Coupon Code 3",
      field: "coupon_code03",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    
    {
      headerName: "Product 4",
      field: "product04",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Quantity 4",
      field: "quantity04",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Price 4",
      field: "price04",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
      valueFormatter: (params) => params.value ? `$${parseFloat(params.value).toFixed(2)}` : '',
    },
    {
      headerName: "Discount 4",
      field: "discount04",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
      valueFormatter: (params) => params.value ? `$${parseFloat(params.value).toFixed(2)}` : '',
    },
    {
      headerName: "Description 4",
      field: "description04",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Coupon Code 4",
      field: "coupon_code04",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    
    {
      headerName: "Product 5",
      field: "product05",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Quantity 5",
      field: "quantity05",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Price 5",
      field: "price05",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
      valueFormatter: (params) => params.value ? `$${parseFloat(params.value).toFixed(2)}` : '',
    },
    {
      headerName: "Discount 5",
      field: "discount05",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
      valueFormatter: (params) => params.value ? `$${parseFloat(params.value).toFixed(2)}` : '',
    },
    {
      headerName: "Description 5",
      field: "description05",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Coupon Code 5",
      field: "coupon_code05",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    
    // Other Information
    {
      headerName: "Reserved",
      field: "reserved",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "DNIS",
      field: "dnis",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Key Code",
      field: "key_code",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Emp Number",
      field: "emp_number",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Continued",
      field: "continued",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Use Prices",
      field: "use_prices",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Voice Recording ID",
      field: "voice_recording_id",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Custom 1",
      field: "custom_1",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Custom 2",
      field: "custom_2",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Custom 3",
      field: "custom_3",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Custom 4",
      field: "custom_4",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Custom 5",
      field: "custom_5",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
  ];

  return (
    <div className="">
      <div className="">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>TigerTech Orders</h4>
            </div>
          </div>

          <ul className="table-top-head">
            <li>
            {lastSyncTime && (
                <p className="text-muted">Last synced: {lastSyncTime}</p>
              )}
            </li>
            
              <li>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>PDF</Tooltip>}
                >
                  <Link
                    onClick={() => {
                      // PDF export functionality would go here
                    }}
                  >
                    <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                  </Link>
                </OverlayTrigger>
              </li>
            
              <li>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Excel</Tooltip>}
                >
                  <Link
                    onClick={() => {
                      // Excel export functionality would go here
                    }}
                  >
                    <ImageWithBasePath src="assets/img/icons/xls.svg" alt="img" />
                  </Link>
                </OverlayTrigger>
              </li>
            <li>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Collapse</Tooltip>}
              >
                <Link
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={() => dispatch(setToogleHeader(!data))}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
          <div className="page-btn">
              <button 
                className="btn btn-added" 
                onClick={handleSyncTigerTech}
                disabled={syncInProgress || !filters || !filters.brands || filters.brands.length === 0}
              >
                <RotateCcw className="me-2 iconsize" />
                Sync TigerTech Orders
              </button>
          </div>
        </div>

        {/* Ag-Grid Table */}
        <div className="card table-list-card">
          <div
            className="ag-theme-quartz"
            style={{ height: "700px", width: "100%", overflow: "auto" }}
          >
            <AgGridReact
              columnDefs={columns}
              rowData={rowData}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
                minWidth: 80
              }}
              pagination={false}
              loading={loading}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              suppressColumnVirtualisation={false}
              suppressRowVirtualisation={false}
              suppressHorizontalScroll={false}
              domLayout="normal"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TigerTechOrders;
